import React from "react";

// Article 2 — Hyperthyroidism in Pregnancy: Can My Baby's Thyroid Be Affected
// Even When My Tests Are Normal?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
    {items.map((i) => (
      <Bullet key={i}>{i}</Bullet>
    ))}
  </ul>
);

export function HyperthyroidismPregnancyFetalThyroid() {
  return (
    <article className="mt-10">
      <P>
        Yes—sometimes. A mother’s thyroid-hormone tests may be normal while <strong>Graves’ antibodies remain in her blood and cross the placenta</strong>. This can matter even after thyroid surgery or radioactive-iodine treatment, when she now takes levothyroxine and no longer appears hyperthyroid.
      </P>
      <P>
        The correct question is not only, “Is the mother’s thyroid controlled?” It is also: <strong>Does she have thyroid-receptor antibodies, is she taking antithyroid medicine, and what is happening to the baby’s thyroid?</strong>
      </P>

      <H2>Graves’ Disease Is More Than a Thyroid-Hormone Result</H2>
      <P>
        Graves’ disease is an autoimmune condition. The immune system produces thyroid-stimulating antibodies—usually reported as <strong>TRAb</strong> or sometimes <strong>TSI</strong>—that activate the thyroid gland.
      </P>
      <P>
        Treatment may control or remove the mother’s overactive thyroid, but it does not always remove these antibodies immediately.
      </P>
      <P>They may remain present after:</P>
      <Bullets
        items={[
          "Radioactive iodine.",
          "Partial or complete thyroid surgery.",
          "A long period without symptoms.",
          "Normal maternal TSH and free T4 results.",
          "A change from antithyroid medicine to levothyroxine.",
        ]}
      />
      <P>
        That is why every woman with a previous history of Graves’ disease should tell her obstetric team, even if the condition seems “finished.” RCOG recommends TRAb measurement in the first trimester for all women with a history of Graves’ disease, including after definitive treatment. <Cite n="1" />
      </P>

      <H2>How Can the Baby Become Hyperthyroid?</H2>
      <P>TRAb can cross the placenta and stimulate the fetal thyroid.</P>
      <P>
        The fetal thyroid becomes increasingly able to respond from around the middle of pregnancy. If maternal antibody levels are sufficiently high, possible fetal findings include:
      </P>
      <Bullets
        items={[
          "Persistent fast heart rate.",
          "Fetal goitre.",
          "Poor growth.",
          "Increased fetal movement.",
          "Advanced bone maturation.",
          "Signs of fetal heart strain.",
          "Hydrops in severe disease.",
        ]}
      />
      <P>
        Fetal or neonatal Graves’ disease is uncommon, but it is clinically important because the mother may have no current symptoms. The American Thyroid Association estimates that fetal or neonatal hyperthyroidism occurs in about 1–5% of pregnancies affected by Graves’ disease, usually when stimulating antibodies are very high. <Cite n="2" />
      </P>
      <P>A normal maternal free T4 does not cancel a strongly positive TRAb result.</P>

      <H2>Can the Baby Also Become Hypothyroid?</H2>
      <P>Yes.</P>
      <P>
        Antithyroid medicines cross the placenta. A dose that suppresses the fetal thyroid too strongly may cause:
      </P>
      <Bullets
        items={[
          "Fetal hypothyroidism.",
          "Fetal goitre.",
          "Reduced fetal thyroid-hormone production.",
        ]}
      />
      <P>The aim is therefore not to make the mother’s thyroid results as low as possible.</P>
      <P>
        When treatment is required, professional guidance recommends the <strong>lowest effective antithyroid-drug dose</strong>, generally targeting maternal free T4 in the upper part of the pregnancy-specific reference range. <Cite n="1,2" />
      </P>
      <P>
        A fetal goitre can occur with either fetal hyperthyroidism or hypothyroidism. The scan appearance alone may not always identify which one is present. The maternal thyroid results, TRAb level, medicine and fetal findings must be interpreted together.
      </P>

      <H2>When Should Graves’ Antibodies Be Checked?</H2>
      <P>Current RCOG guidance recommends:</P>
      <Bullets
        items={[
          "TRAb in the first trimester for every woman with previous Graves’ disease.",
          "Repeat testing at about 20 weeks when TRAb is positive or the woman is taking antithyroid medicine.",
        ]}
      />
      <ul className="mt-2 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
        <Bullet>
          A further measurement at about 28 weeks in the same higher-risk group. <Cite n="1" />
        </Bullet>
      </ul>
      <P>
        The American Thyroid Association similarly recommends early testing after previous surgery or radioactive iodine, with repeat assessment around 18–22 weeks when antibodies are elevated and later testing when they remain raised. <Cite n="2" />
      </P>
      <P>
        The exact laboratory assay and threshold should be stated. A result described only as “positive” is less useful than knowing how far it is above that laboratory’s upper limit.
      </P>

      <H2>Which Pregnancies Need Closer Fetal Surveillance?</H2>
      <P>Closer monitoring is appropriate when there has been:</P>
      <Bullets
        items={[
          "Uncontrolled Graves’ hyperthyroidism.",
          "Antithyroid-drug treatment during pregnancy.",
          "TRAb approximately three times or more above the laboratory threshold.",
          "A previous baby affected by thyroid disease.",
          "A suspected fetal goitre or persistent tachycardia.",
          "Abnormal fetal growth or another concerning finding.",
        ]}
      />
      <P>
        Following its May 2026 correction, RCOG advises serial fetal-biometry and umbilical-artery Doppler assessment at monthly intervals <strong>starting from 26–28 weeks onwards</strong> for pregnancies in these higher-risk groups. <Cite n="1,3" />
      </P>
      <P>Specialist ultrasound may assess:</P>
      <Bullets
        items={[
          "Fetal growth.",
          "Heart rate and rhythm.",
          "Thyroid size.",
          "Amniotic fluid.",
          "Fetal activity.",
          "Signs of heart failure.",
          "Umbilical-artery Doppler.",
        ]}
      />
      <P>
        More scans are not automatically necessary when Graves’ antibodies are negative, maternal disease is controlled and no other risk exists.
      </P>

      <H2>What Happens After Birth?</H2>
      <P>The effect of maternal antibodies does not stop immediately at delivery.</P>
      <P>
        A newborn may become hyperthyroid after maternal antithyroid medicine clears from the baby’s circulation while stimulating antibodies remain. Alternatively, medication exposure may contribute to neonatal hypothyroidism.
      </P>
      <P>
        RCOG recommends thyroid-function monitoring soon after birth and again at one to two weeks for babies whose mothers have known Graves’ disease, received antithyroid medicine during pregnancy or had raised TRAb. <Cite n="1,4" />
      </P>
      <P>The neonatal team should know the maternal:</P>
      <Bullets
        items={[
          "Graves’ history.",
          "Latest TRAb result.",
          "Antithyroid medicine and dose.",
          "Fetal ultrasound findings.",
          "Previous affected-baby history.",
        ]}
      />
      <P>A normal newborn examination alone may not complete the assessment.</P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> In Graves’ disease, the mother’s thyroid report and the baby’s thyroid risk are related—but they are not identical. A woman may be clinically well and biochemically normal while antibodies still cross the placenta. Safe care reads four things together: maternal free T4, TRAb, antithyroid treatment and fetal findings.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Chan S-Y, Marsh MS, Gilbert J, et al; Royal College of Obstetricians and Gynaecologists. Management of Thyroid Disorders in Pregnancy. Green-top Guideline No. 76. BJOG. 2025;132:e130–e161, with correction published May 2026.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.rcog.org.uk/guidance/browse-all-guidance/green-top-guidelines/management-of-thyroid-disorders-in-pregnancy-no-76/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.rcog.org.uk/guidance/browse-all-guidance/green-top-guidelines/management-of-thyroid-disorders-in-pregnancy-no-76/</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] American Thyroid Association. Hyperthyroidism in Pregnancy. Current online patient and professional information.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.thyroid.org/hyperthyroidism-in-pregnancy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.thyroid.org/hyperthyroidism-in-pregnancy/</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Correction to “Management of Thyroid Disorders in Pregnancy.” BJOG. First published 8 May 2026;133:1712. doi:10.1111/1471-0528.70259.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://obgyn.onlinelibrary.wiley.com/doi/10.1111/1471-0528.70259"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://obgyn.onlinelibrary.wiley.com/doi/10.1111/1471-0528.70259</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Royal College of Obstetricians and Gynaecologists. Thyroid Problems in Pregnancy. Patient information published December 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.rcog.org.uk/for-the-public/browse-our-patient-information/thyroid-problems-in-pregnancy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.rcog.org.uk/for-the-public/browse-our-patient-information/thyroid-problems-in-pregnancy/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific endocrine, obstetric, fetal-medicine or neonatal advice. Do not stop or alter thyroid medicine because of this page. Seek prompt medical assessment for severe palpitations, chest pain, fainting, breathlessness, fever with marked agitation, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
